import { Link } from 'react-router-dom';
import { ArrowUpDown, MoreHorizontal, Trash2, Send, Clock, X } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '~/components/ui/button';
import { Badge, type BadgeProps } from '~/components/ui/badge';
import { createSelectColumn } from '~/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { progressLabel } from '../FamilyDetail/checklistWords';
import { ACCESS_LABELS, isNewStyle } from './enrollmentPhases';
import type { FamilyEnrollmentRow, FamilyColumnHandlers } from './types';

const stateColors: Record<string, string> = {
  DRAFT: 'secondary',
  READY_TO_INVITE: 'outline',
  INVITED: 'default',
  IN_PROGRESS: 'default',
  NEEDS_REVIEW: 'destructive',
  APPROVED: 'default',
  PACKET_SENT: 'default',
  PACKET_VIEWED: 'default',
  PACKET_SUBMITTED: 'default',
  PACKET_COMPLETED: 'default',
  DECLINED: 'destructive',
};

const stateLabels: Record<string, string> = {
  DRAFT: 'Draft',
  READY_TO_INVITE: 'Ready to Invite',
  INVITED: 'Invited',
  IN_PROGRESS: 'In Progress',
  NEEDS_REVIEW: 'Needs Review',
  APPROVED: 'Approved',
  PACKET_SENT: 'Packet Sent',
  PACKET_VIEWED: 'Packet Viewed',
  PACKET_SUBMITTED: 'Packet Submitted',
  PACKET_COMPLETED: 'Completed',
  DECLINED: 'Declined',
};

const accessColors: Record<string, string> = {
  NOT_INVITED: 'secondary',
  ENROLLING: 'default',
  ENROLLED: 'default',
  DECLINED: 'destructive',
};

// A year opened by the checklist invite, and every year once the school enrolls
// with the checklist, is shown by its access, not the old flow's state.
const statusKey = (row: FamilyEnrollmentRow, switchOn?: boolean) => (isNewStyle(row, switchOn) ? `ACCESS_${row.access}` : row.state);
const statusLabel = (row: FamilyEnrollmentRow, switchOn?: boolean) =>
  isNewStyle(row, switchOn) ? ACCESS_LABELS[row.access] ?? row.access : stateLabels[row.state] || row.state;

const kindLabels: Record<string, string> = {
  NEW_ADMISSION: 'New',
  RE_ENROLLMENT: 'Returning',
};

export const getFamilyColumns = (schoolSlug: string | undefined, { onDelete, onQueue, onUnqueue, onSendInvite, checklistEnrollmentOn }: FamilyColumnHandlers = {}): ColumnDef<FamilyEnrollmentRow>[] => [
  createSelectColumn<FamilyEnrollmentRow>(),
  {
    id: 'familyName',
    accessorFn: (row) => row.family?.name ?? '',
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Family
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const family = row.original.family;
      return (
        <Link
          to={`/app/${schoolSlug}/families/${family?.slug}/enrollment`}
          className="font-medium hover:underline"
        >
          {family?.name}
        </Link>
      );
    },
  },
  {
    accessorKey: 'enrollmentKind',
    header: 'Type',
    cell: ({ row }) => (
      <Badge variant="outline">
        {kindLabels[row.original.enrollmentKind] || row.original.enrollmentKind}
      </Badge>
    ),
    meta: {
      facet: {
        title: 'Type',
        getKey: (row: FamilyEnrollmentRow) => row.enrollmentKind,
        getLabel: (row: FamilyEnrollmentRow) => kindLabels[row.enrollmentKind] || row.enrollmentKind,
      },
    },
    filterFn: (row, id, value) => {
      return (value as string[]).includes(row.getValue(id));
    },
  },
  {
    id: 'state',
    accessorFn: (row) => statusKey(row, checklistEnrollmentOn),
    header: 'Status',
    cell: ({ row }) => (
      <Badge
        variant={
          ((isNewStyle(row.original, checklistEnrollmentOn) ? accessColors[row.original.access] : stateColors[row.original.state]) || 'secondary') as BadgeProps["variant"]
        }
      >
        {statusLabel(row.original, checklistEnrollmentOn)}
      </Badge>
    ),
    meta: {
      facet: {
        title: 'Status',
        getKey: (row: FamilyEnrollmentRow) => statusKey(row, checklistEnrollmentOn),
        getLabel: (row: FamilyEnrollmentRow) => statusLabel(row, checklistEnrollmentOn),
      },
    },
    filterFn: (row, id, value) => {
      return (value as string[]).includes(row.getValue(id));
    },
  },
  {
    id: 'checklist',
    header: 'Checklist',
    accessorFn: (row) => progressLabel(row.checklist),
    cell: ({ row }) => {
      const checklist = row.original.checklist;
      const waitingOn = checklist?.progress.waitingOn ?? [];
      return (
        <div>
          <div className="flex items-center gap-2">
            <span>{progressLabel(checklist)}</span>
            {checklist?.late && <Badge variant="destructive">Late</Badge>}
          </div>
          {waitingOn.length > 0 && (
            <div className="text-xs text-muted-foreground">Waiting on {waitingOn[0]}</div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'studentCount',
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Students
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="text-center">{row.original.studentCount}</div>
    ),
  },
  {
    accessorKey: 'family.primaryContactEmail',
    header: 'Primary Email',
    cell: ({ row }) => (
      <div className="text-muted-foreground">
        {row.original.family?.primaryContactEmail || '-'}
      </div>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        Last Updated
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    ),
    cell: ({ row }) => {
      const date = new Date(row.original.updatedAt as string);
      return <div>{date.toLocaleDateString()}</div>;
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const enrollment = row.original;
      const family = enrollment.family;
      const canDelete = enrollment.canDelete;
      const oldFlow = !isNewStyle(enrollment) && !checklistEnrollmentOn;
      const canQueue = oldFlow && enrollment.canQueueForInvite;
      const canUnqueue = oldFlow && enrollment.state === 'READY_TO_INVITE' && enrollment.inviteStatus !== 'SENT';
      const canSendInvite = oldFlow && enrollment.canSendInvite;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(enrollment.id)}
            >
              Copy ID
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={`/app/${schoolSlug}/families/${family?.slug}/enrollment`}>
                View Enrollment
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/app/${schoolSlug}/families/${family?.slug}`}>
                View Family
              </Link>
            </DropdownMenuItem>

            {/* Invite workflow actions */}
            {canQueue && onQueue && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onQueue(enrollment)}>
                  <Clock className="mr-2 h-4 w-4" />
                  Queue for Invite
                </DropdownMenuItem>
              </>
            )}
            {canUnqueue && onUnqueue && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onUnqueue(enrollment)}>
                  <X className="mr-2 h-4 w-4" />
                  Remove from Queue
                </DropdownMenuItem>
              </>
            )}
            {canSendInvite && onSendInvite && (
              <DropdownMenuItem onClick={() => onSendInvite(enrollment)}>
                <Send className="mr-2 h-4 w-4" />
                Send Invite Now
              </DropdownMenuItem>
            )}

            {canDelete && onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(enrollment)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Enrollment
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
